import { ofType, combineEpics } from "redux-observable";
import { concat, from, Observable, of } from "rxjs";
import {
  map,
  switchMap,
  concatMap,
  mergeMap,
  startWith,
  filter
} from "rxjs/operators";
import { responseFrom, ResponseAction, request } from "../../ajax";
import { Action } from "../../Store";
import { ErrorResponse } from "../../utils";
import { Actions } from "./state/actions";
import { State } from "./state/state";

interface PostRequestWithKey {
  key: string;
  post: State.ServerRequest;
}

// These epic patterns described by react-observable maintainer here:
// https://stackoverflow.com/questions/48261438/composing-and-sequencing-multiple-epics-in-redux-observable/48307271#48307271
// This allows to run through ajax reducers.
// TODO - replace the switchMap pipe with a pipable operator so we can reuse this pattern
export const getEpic = (action$: Observable<Action>): Observable<Action> =>
  action$.pipe(
    ofType(Actions.REQUEST_GET),
    switchMap(() =>
      action$.pipe(
        responseFrom("/ATX/timers/schedule"),
        map((a: ResponseAction<State.ServerResponse>) => {
          return Actions.getResponse({ data: a.data });
        }),
        startWith(request("/ATX/timers/schedule"))
      )
    )
  );

export const addEpic = (action$: Observable<Action>): Observable<Action> =>
  action$.pipe(
    ofType(Actions.REQUEST_ADD),
    filter((a: Actions.AddRequest) => !!Object.keys(a.data).length),
    switchMap((req: Actions.AddRequest) =>
      action$.pipe(
        responseFrom(`/ATX/timers/schedule`, "POST"),
        switchMap((a: ResponseAction<ErrorResponse>) =>
          concat(
            of(Actions.addResponse({ data: a.data })),
            of(Actions.getRequest()),
            of(request("/ATX/exe/save", { save: 1 }))
          )
        ),
        startWith(request("/ATX/timers/schedule", req.post))
      )
    )
  );

export const removeEpic = (action$: Observable<Action>): Observable<Action> =>
  action$.pipe(
    ofType(Actions.REQUEST_DELETE),
    switchMap((req: Actions.DeleteRequest) =>
      action$.pipe(
        responseFrom(`/ATX/timers/schedule/${req.data}`, "DELETE"),
        switchMap((a: ResponseAction<ErrorResponse>) =>
          concat(
            of(Actions.addResponse({ data: a.data })),
            of(Actions.getRequest()),
            of(request("/ATX/exe/save", { save: 1 }))
          )
        ),
        startWith(request(`DELETE /ATX/timers/schedule/${req.data}`))
      )
    )
  );

export const editEpic = (action$: Observable<Action>): Observable<Action> =>
  action$.pipe(
    ofType(Actions.REQUEST_EDIT),
    filter((a: Actions.EditRequest) => !!Object.keys(a.data).length),
    mergeMap((a: Actions.EditRequest) =>
      concat(
        from(
          Object.keys(a.post || {}).map(key => {
            return { key, post: a.post };
          })
        ).pipe(
          concatMap((req: PostRequestWithKey) =>
            action$.pipe(
              responseFrom(`/ATX/timers/schedule`, "POST"),
              switchMap((a: ResponseAction<ErrorResponse>) =>
                concat(
                  of(Actions.editResponse({ data: a.data })),
                  of(Actions.getRequest()),
                  of(request("/ATX/exe/save", { save: 1 }))
                )
              ),
              startWith(request(`/ATX/timers/schedule`, req.post))
            )
          )
        ),
        of(Actions.editResponseEnd())
      )
    )
  );

export default combineEpics(getEpic, editEpic, addEpic, removeEpic);
